import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Alert, Button, Dropdown, Select, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeft, Boxes, CalendarClock, ChevronDown, Coins, Pencil, Wallet } from 'lucide-react';
import Layout from '../../Layout';
import { useKho } from './khoStore';
import {
  DOC_TYPE, ITEM_KIND, avgCost, fmtMoney, fmtNum, fmtQty, itemById, itemStocks, ledger, serviceById, supplierById, whById, type LedgerRow, type Lot,
} from './khoLogic';
import { ExpiryText, ItemFlags, Kpi, PageHeader, Panel, docUrl, fmtDate, fmtDateTime, newDocUrl, useKhoPerms } from './khoUi';

/** Thẻ kho của một mặt hàng: tồn theo kho/lô, lịch sử nhập xuất, định mức, khách đã dùng */
export default function ItemDetailPage() {
  const s = useKho();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canCreate, canEdit } = useKhoPerms();
  const item = itemById(s, params.get('id') || '');
  const wh = params.get('wh') || undefined;
  const now = Date.now();

  if (!item) return <Layout><Alert type="warning" showIcon message="Không tìm thấy mặt hàng" action={<Link to="/warehouses">Về tồn kho</Link>} /></Layout>;

  const all = itemStocks(s, now).find(r => r.item.id === item.id)!;
  const rows = ledger(s, item.id, wh).reverse().map((m, i) => ({ ...m, key: `${m.docId}-${m.warehouseId}-${m.lotNo}-${i}` }));
  const used = ledger(s, item.id).filter(m => m.type === 'issue_service' || m.type === 'issue_sale').reverse().slice(0, 12);
  const norms = Object.entries(s.norms).flatMap(([sid, ls]) => ls.filter(l => l.itemId === item.id).map(l => ({ service: serviceById(s, sid), qty: l.qty }))).filter(x => x.service);
  const cost = avgCost(s, item.id);
  const extra = (lot?: Lot) => `item=${item.id}${lot ? `&wh=${lot.warehouseId}&lot=${encodeURIComponent(lot.lotNo)}` : wh ? `&wh=${wh}` : ''}`;

  const lotColumns: ColumnsType<Lot> = [
    { title: 'Kho', key: 'wh', render: (_, l) => whById(s, l.warehouseId)?.name },
    { title: 'Số lô', dataIndex: 'lotNo', key: 'lot', render: v => <span className="font-mono text-xs">{v}</span> },
    { title: 'Hạn dùng', key: 'exp', render: (_, l) => <ExpiryText expiry={l.expiry} now={now} /> },
    { title: 'Số lượng', key: 'qty', align: 'right', render: (_, l) => <b>{fmtQty(item, l.qty)}</b> },
    { title: 'Giá trị', key: 'v', align: 'right', render: (_, l) => fmtMoney(l.qty * l.unitCost) },
  ];

  const ledgerColumns: ColumnsType<LedgerRow & { key: string }> = [
    { title: 'Thời gian', key: 'at', width: 140, render: (_, m) => fmtDateTime(m.at) },
    { title: 'Phiếu', key: 'code', width: 140, render: (_, m) => <Link to={docUrl(m.docId)} className="text-[#3e91ab]">{m.code}</Link> },
    { title: 'Phát sinh', key: 'type', width: 130, render: (_, m) => DOC_TYPE[m.type].label },
    { title: 'Kho', key: 'wh', render: (_, m) => whById(s, m.warehouseId)?.name },
    { title: 'Lô', dataIndex: 'lotNo', key: 'lot', width: 110, render: v => <span className="font-mono text-xs">{v}</span> },
    { title: 'Nhập', key: 'in', align: 'right', width: 90, render: (_, m) => (m.qty > 0 ? <span className="text-emerald-600">+{fmtNum(m.qty)}</span> : '') },
    { title: 'Xuất', key: 'out', align: 'right', width: 90, render: (_, m) => (m.qty < 0 ? <span className="text-rose-600">{fmtNum(m.qty)}</span> : '') },
    { title: 'Tồn', key: 'bal', align: 'right', width: 90, render: (_, m) => <b>{fmtNum(m.balance)}</b> },
    {
      title: 'Đối tượng', key: 'who', render: (_, m) => (
        <span className="text-xs text-gray-500">
          {m.customer?.name || (m.otherWarehouseId ? `${m.qty > 0 ? 'từ' : 'tới'} ${whById(s, m.otherWarehouseId)?.name}` : '')}
          {m.serviceId ? ` · ${serviceById(s, m.serviceId)?.name}` : ''}
        </span>
      ),
    },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title={item.name} desc={`${item.code} · ${ITEM_KIND[item.kind].label}${item.brand ? ` · ${item.brand}` : ''}${item.active ? '' : ' · NGỪNG SỬ DỤNG'}`}
          extra={<>
            <Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>Quay lại</Button>
            {canEdit && <Button icon={<Pencil size={14} />} onClick={() => navigate(`/warehouse-items?edit=${item.id}`)}>Sửa thông tin</Button>}
            {canCreate && (
              <Dropdown trigger={['click']} menu={{
                items: [
                  { key: 'import', label: 'Nhập thêm' }, { key: 'transfer', label: 'Chuyển kho' },
                  { key: 'issue_sale', label: 'Xuất bán', disabled: item.kind !== 'cosmetic' }, { key: 'dispose', label: 'Xuất hủy' },
                ],
                onClick: e => navigate(e.key === 'import' ? newDocUrl('import', `items=${item.id}:${item.packSize || 1}`) : newDocUrl(e.key as 'transfer', extra())),
              }}>
                <Button type="primary">Tạo phiếu <ChevronDown size={14} /></Button>
              </Dropdown>
            )}
          </>} />
        <div className="-mt-2"><ItemFlags item={item} /></div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <Kpi icon={<Boxes size={18} />} tone={all.low ? 'bg-orange-50 text-orange-600' : 'bg-sky-50 text-sky-600'} label="Tổng tồn (mọi kho)" value={fmtQty(item, all.qty)}
            hint={item.minStock ? `Tối thiểu ${fmtNum(item.minStock)} ${item.unit}${all.low ? ' · SẮP HẾT' : ''}` : undefined} />
          <Kpi icon={<Wallet size={18} />} tone="bg-emerald-50 text-emerald-600" label="Giá trị tồn" value={fmtMoney(all.value)} />
          <Kpi icon={<Coins size={18} />} tone="bg-violet-50 text-violet-600" label={`Giá vốn bình quân / ${item.unit}`} value={fmtMoney(cost)}
            hint={item.price ? `Giá bán ${fmtMoney(item.price)} · lãi ${Math.round(((item.price - cost) / item.price) * 100)}%` : item.packSize ? `${fmtMoney(cost * item.packSize)} / ${item.packUnit}` : undefined} />
          <Kpi icon={<CalendarClock size={18} />} tone="bg-amber-50 text-amber-600" label="Hạn dùng gần nhất" value={all.nearestExpiry ? fmtDate(new Date(all.nearestExpiry).getTime()) : '—'}
            hint={all.nearestExpiry ? undefined : 'Không quản lý hạn'} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
          <div className="flex flex-col gap-4 min-w-0">
            <Panel title="Tồn theo kho và lô" extra={<span className="text-xs text-gray-400">Xếp theo hạn dùng — lô trên cùng xuất trước</span>}>
              <Table<Lot> rowKey="key" size="small" pagination={false} columns={lotColumns} dataSource={all.lots} scroll={{ x: 640 }}
                locale={{ emptyText: 'Hết hàng ở mọi kho' }} />
            </Panel>
            <Panel title="Thẻ kho (lịch sử nhập xuất)"
              extra={<Select allowClear size="small" className="w-[220px]" placeholder="Tất cả kho" value={wh}
                onChange={v => { const n = new URLSearchParams(params); if (v) n.set('wh', v); else n.delete('wh'); setParams(n, { replace: true }); }}
                options={s.warehouses.map(w => ({ value: w.id, label: w.name }))} />}>
              <Table<LedgerRow & { key: string }> rowKey="key" size="small" columns={ledgerColumns} dataSource={rows} scroll={{ x: 960 }}
                pagination={{ pageSize: 15, hideOnSinglePage: true }} />
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            <Panel title="Thông tin hàng">
              <dl className="p-4 grid grid-cols-2 gap-3 text-sm m-0">
                <Info k="Đơn vị xuất" v={item.unit} />
                <Info k="Quy cách" v={item.packUnit && item.packSize ? `1 ${item.packUnit} = ${fmtNum(item.packSize)} ${item.unit}` : '—'} />
                <Info k="Nhà cung cấp" v={supplierById(s, item.supplierId)?.name || '—'} />
                <Info k="Bảo quản" v={item.cold ? 'Lạnh 2–8°C' : 'Nhiệt độ thường'} />
                <Info k="Kiểm soát" v={item.controlled ? 'Thuốc kiểm soát' : 'Không'} />
                <Info k="Giá vốn mặc định" v={fmtMoney(item.cost)} />
              </dl>
              {item.note && <p className="px-4 pb-4 text-xs text-gray-500 m-0">{item.note}</p>}
            </Panel>
            <Panel title="Dùng trong định mức dịch vụ">
              <div className="p-2">
                {norms.length ? norms.map(n => (
                  <Link key={n.service!.id} to={`/service-norms?service=${n.service!.id}`} className="flex justify-between px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-sm">
                    <span className="text-gray-800 dark:text-gray-200">{n.service!.name}</span><span className="text-gray-500">{fmtNum(n.qty)} {item.unit}/buổi</span>
                  </Link>
                )) : <p className="text-xs text-gray-400 px-2 py-2 m-0">Chưa gắn vào định mức dịch vụ nào</p>}
              </div>
            </Panel>
            <Panel title="Khách đã dùng gần đây" extra={<Link to={`/inventory?tab=trace&q=${encodeURIComponent(item.code)}`} className="text-xs text-[#3e91ab]">Truy xuất lô</Link>}>
              <div className="p-2">
                {used.length ? used.map((m, i) => (
                  <div key={i} className="px-2 py-1.5 text-sm flex justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-gray-800 dark:text-gray-200">{m.customer?.name || 'Khách lẻ'}</div>
                      <div className="text-[11px] text-gray-400">{fmtDate(m.at)} · lô {m.lotNo}{m.serviceId ? ` · ${serviceById(s, m.serviceId)?.name}` : ''}</div>
                    </div>
                    <span className="text-gray-500 whitespace-nowrap">{fmtNum(-m.qty)} {item.unit}</span>
                  </div>
                )) : <p className="text-xs text-gray-400 px-2 py-2 m-0">Chưa có</p>}
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Info = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <div><dt className="text-xs text-gray-400">{k}</dt><dd className="m-0 text-gray-800 dark:text-gray-200">{v}</dd></div>
);
